import type { Dispatch } from "react";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import type { EditorAction } from "../editor/state";
import type { DraftPlan } from "../editor/types";

/**
 * The venue `AutoAssignPolicy` config surface (R6): oversize cap + spread-across-zones. This is the
 * AUTHORING surface only — smallest-fitting-first and the cap ENFORCEMENT are fixed platform behaviour
 * owned by the model's AutoAssignPort. The values round-trip through the whole-aggregate save.
 */
export interface PolicyPanelProps {
  plan: DraftPlan;
  disabled: boolean;
  dispatch: Dispatch<EditorAction>;
}

export function PolicyPanel({ plan, disabled, dispatch }: PolicyPanelProps) {
  const { t } = useTranslation();
  const { oversizeCap, spreadAcrossZones } = plan.policy;

  return (
    <section
      aria-label={t("floorPlan.policy.title")}
      className={css({ display: "flex", flexDirection: "column", gap: "2" })}
    >
      <h3 className={css({ fontSize: "md", fontWeight: "semibold" })}>
        {t("floorPlan.policy.title")}
      </h3>

      <label
        className={css({
          display: "flex",
          flexDirection: "column",
          gap: "1",
          fontSize: "sm",
          maxW: "xs",
        })}
      >
        {t("floorPlan.policy.oversizeCap")}
        <input
          type="number"
          min={0}
          className={css({ px: "2", py: "1", borderWidth: "1px", rounded: "md" })}
          disabled={disabled}
          value={oversizeCap}
          onChange={(e) =>
            dispatch({
              type: "SET_POLICY",
              oversizeCap: Number(e.target.value) || 0,
              spreadAcrossZones,
            })
          }
        />
      </label>
      <p className={css({ fontSize: "xs", color: "fg.muted" })}>
        {t("floorPlan.policy.oversizeHint")}
      </p>

      <label className={css({ display: "flex", gap: "2", alignItems: "center", fontSize: "sm" })}>
        <input
          type="checkbox"
          disabled={disabled}
          checked={spreadAcrossZones}
          onChange={(e) =>
            dispatch({ type: "SET_POLICY", oversizeCap, spreadAcrossZones: e.target.checked })
          }
        />
        {t("floorPlan.policy.spread")}
      </label>
    </section>
  );
}
